import React, {
  useEffect, useCallback, useState, useMemo,
} from 'react';
import { Row, Spin } from 'antd';
import SVG from 'svgjs';
import * as PropTypes from 'prop-types';
import ViaSvg, { useViaSvg } from './ViaSvg';
import './VehicleDiagram.css';
import diagramAnimation from '../../sections/secured/developer/diagram/diagram-animation';
import { damageAreaShape } from '../../sections/secured/configure/inspection/config-util';
import {
  DIAGRAM_LOCATION, MARKER_GROUP_ID, svgOpenTag, svgCloseTag, MARKER_SVG, SELECTED_AREA_CLASS, createUnionRect, diagramPaddingPx, bboxEquals, calculateElementBboxAfterTransform,
} from './diagram-util';


const useDiagramRef = () => {
  const [diagramElement, setDiagramElement] = useState();
  const diagramRef = useCallback((element) => {
    if (element) setDiagramElement(element);
  }, [setDiagramElement]);

  return {
    diagramRef,
    diagramElement,
  };
};

/**
 * An interactable Vehicle Diagram component.
 * Loads the diagram using the props.diagramCode and then marks the various damages on the diagram.
 * @param {string} props.diagramCode The code for the diagram. Used for loading the diagram.
 * @param {object} [props.selectedArea] The area that is currently focused in the diagram. (Optional)
 * @param {object} [props.newDamages] The new damages to be marked on the diagram. (Optional)
 * @param {object} [props.existingDamages] The existing damages to be marked on the diagram. (Optional)
 * @param {func} [onDamageSelect] A callback function that takes the selected damage damageId as an argument. (Optional)
 * @param {object} [style] The JS style object for the component. (Optional)
 */
function VehicleDiagram({
  diagramCode, selectedArea, newDamages, existingDamages, onDamageSelect, style, className, external, viewOnly
}) {
  // Load the diagram html from the server.
  const [loading, svg] = useViaSvg(diagramCode ? `${DIAGRAM_LOCATION}/${external ? 'external/' : ''}${diagramCode}` : null);

  // Create a ref to the SVG dom element.
  const { diagramRef, diagramElement } = useDiagramRef();

  // The SVG-JS library wrapper around the diagram element.
  const [diagramSVG, setDiagramSVG] = useState();

  // The animation wrapper around the diagramSVG element.
  const memoAnimation = useMemo(() => ({ animation: null }), []);

  // Do effect on dom mount of diagram.
  useEffect(() => {
    if (!diagramElement) return; // Element has not been rendered yet.

    // Add class names from props.
    diagramElement.setAttribute('class', `kipper-svg-root ${className}`);

    // Work around for diagrams that have been given a static width/height.
    diagramElement.childNodes.forEach(s => {
      if (s.removeAttribute) {
        s.removeAttribute('width');
        s.removeAttribute('height');
      }
    });

    // Apply inline styles from props.
    Object.keys(style).forEach((key) => {
      diagramElement.style[key] = style[key];
    });

    // We alway wrap the SVG with another SVG. So get the child svg here.
    // Then adopt that SVG into the SVG-JS library.
    setDiagramSVG(SVG.adopt(diagramElement).children()[0]);
  }, [diagramElement, style, className]);

  // Do effect once we have wrapped the SVG with the SVG-JS library.
  useEffect(() => {
    if (!diagramSVG) return;

    // Initialise our animation wrapper on the diagram.
    memoAnimation.animation = diagramAnimation(diagramSVG);
  }, [diagramSVG, memoAnimation]);


  // Do effect when damages are updated.
  useEffect(() => {
    if (!diagramSVG) return; // The diagram may not be mounted on the DOM yet.

    // Create a group in the SVG to place the markers.
    let markerGroup = diagramSVG.select(`#${MARKER_GROUP_ID}`).members[0];
    if (!markerGroup) {
      markerGroup = diagramSVG.group().id(MARKER_GROUP_ID);
      diagramSVG.add(markerGroup);
    }
    // Remove any existing markers.
    markerGroup.clear();

    // Convert into a general list of damages that can be displayed.
    // We need to sort by Y coordinate so they overlap nicely.
    const allDamages = [...newDamages, ...existingDamages].filter(damage => damage.damageXCoordinate && damage.damageYCoordinate);
    allDamages.sort((d1, d2) => d1.damageYCoordinate - d2.damageYCoordinate);

    // Generate a marker for each diagram location.
    const oParser = new DOMParser(); // For creating the marker element
    allDamages.forEach((damage) => {
      // If it has no meaningful coordinates then there's no reason to attempt showing it.
      if (damage.damageXCoordinate == null || damage.damageYCoordinate == null) return;

      // If it already exists then don't re-render it,
      const renderedMarker = SVG.get(damage.damageId);
      if (renderedMarker) return;

      // Create a marker element in the DOM.
      let markerRawHtml;
      if (newDamages.includes(damage)) {
        markerRawHtml = `${svgOpenTag}${MARKER_SVG.NEW_DAMAGE}${svgCloseTag}`;
      } else if (existingDamages.includes(damage)) {
        markerRawHtml = `${svgOpenTag}${MARKER_SVG.PRE_EXIST_DAMAGE}${svgCloseTag}`;
      } else {
        markerRawHtml = `${svgOpenTag}${MARKER_SVG.TEMPORARY}${svgCloseTag}`;
      }
      const markerElement = oParser.parseFromString(markerRawHtml, 'image/svg+xml');

      // Wrap the marker element with the SVG-JS library.
      const markerG = markerGroup.group()
        .id(damage.damageId) // Id for easier selects
        .addClass('marker') // General styling class
        .transform({ x: damage.damageXCoordinate, y: damage.damageYCoordinate }); // Position on the diagram.
      const marker = SVG.adopt(markerElement.children[0]);

      // Add to the group and add handlers/
      markerG.add(marker);
      marker.on('click', (e) => { e.stopPropagation(); onDamageSelect(damage.damageId); });
    });
  }, [diagramSVG, newDamages, existingDamages, onDamageSelect]);


  // Do effect when  selected area changes.
  useEffect(() => {
    if (!diagramSVG) return; // Diagram is not mounted yet.

    let goalBox = diagramSVG.bbox();
    diagramSVG.select(`.${SELECTED_AREA_CLASS}`).removeClass(SELECTED_AREA_CLASS); // Remove the styling on the old selected areas.
    if (selectedArea) {
      const { members } = diagramSVG.select(`#${selectedArea.damageAreaCode}`);
      let goalRect = members.length > 0
        ? calculateElementBboxAfterTransform(diagramSVG.node, members[0].node)
        : null;
      members.forEach((area) => {
        area.addClass(SELECTED_AREA_CLASS); // Add styling to make it look selected.
        const areaBBox = calculateElementBboxAfterTransform(diagramSVG.node, area.node);
        goalRect = createUnionRect(goalRect, areaBBox);
      });

      if ((diagramSVG.bbox().width > (goalRect.width() + diagramPaddingPx)) && (diagramSVG.bbox().height > (goalRect.height() + diagramPaddingPx))) {
        goalBox = goalRect.bbox();
      }
    }

    const addPadding = bboxEquals(goalBox, diagramSVG.bbox());
    memoAnimation.animation.setGoalBox(goalBox, addPadding);
    memoAnimation.animation.start();
  }, [diagramSVG, selectedArea, memoAnimation]);

  return (loading
    ? <Row type="flex" justify="center"><Spin /></Row>
    : <div style={{ height: '100%', display: loading ? 'none' : '' }}><ViaSvg ref={diagramRef} safeSvgHtml={svg} /></div>
  );
}


VehicleDiagram.propTypes = {
  diagramCode: PropTypes.number.isRequired,
  selectedArea: damageAreaShape,
  newDamages: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  existingDamages: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onDamageSelect: PropTypes.func,
  style: PropTypes.shape({}), // Too many shapes to define here. Any JS-CSS is valid.
  className: PropTypes.string,
  external: PropTypes.bool,
  viewOnly: PropTypes.bool,
};

VehicleDiagram.defaultProps = {
  newDamages: [],
  selectedArea: null,
  existingDamages: [],
  onDamageSelect: () => {},
  style: {},
  className: '',
  external: false,
  viewOnly: false,
};

export default VehicleDiagram;
